import { useQuery, useMutation } from '@tanstack/react-query';
import { apis } from 'apis/apis';
import {
	ITrendParams,
	IHighlightParams,
	IRankingFilterParams,
	ICreatorRankingFilterParams,
	ITrendingContentParams,
} from 'type/tracking';

export const useGetTrackingList = () => {
	return useQuery({
		queryKey: ['tracking-list'],
		queryFn: () => apis.getTrackingList(),
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
	});
};

export const useGetTrackingSummary = (
	code?: string,
	startDate?: string,
	endDate?: string,
) => {
	return useQuery({
		queryKey: ['tracking-summary', code, startDate, endDate],
		queryFn: () => {
			if (!code) return undefined;
			return apis.getTrackingSummary(code, startDate, endDate);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code,
	});
};

export const useGetContentSummary = (
	code?: string,
	startDate?: string,
	endDate?: string,
) => {
	return useQuery({
		queryKey: ['tracking-content-summary', code, startDate, endDate],
		queryFn: () => {
			if (!code) return undefined;
			return apis.getContentSummary(code, startDate, endDate);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code,
	});
};

export const useGetTrackingTrend = (code?: string, params?: ITrendParams) => {
	return useQuery({
		queryKey: ['tracking-trend', code, params],
		queryFn: () => {
			if (!code || !params) return undefined;
			return apis.getTrackingTrend(code, params);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code && !!params,
	});
};

export const useGetTrackingHighlights = (code?: string, params?: IHighlightParams) => {
	return useQuery({
		queryKey: ['tracking-highlights', code, params],
		queryFn: () => {
			if (!code || !params) return undefined;
			return apis.getTrackingHighlights(code, params);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code && !!params,
	});
};

export const useGetContentRanking = (code?: string, params?: IRankingFilterParams) => {
	return useQuery({
		queryKey: ['tracking-content-ranking', code, params],
		queryFn: () => {
			if (!code || !params) return undefined;
			return apis.getContentRanking(code, params);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code && !!params,
	});
};

export const useGetCreatorRanking = (
	code?: string,
	params?: ICreatorRankingFilterParams,
) => {
	return useQuery({
		queryKey: ['tracking-creator-ranking', code, params],
		queryFn: () => {
			if (!code || !params) return undefined;
			return apis.getCreatorRanking(code, params);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code && !!params,
	});
};

export const useGetTrendingContents = (
	code?: string,
	params?: ITrendingContentParams,
) => {
	return useQuery({
		queryKey: ['tracking-trending-contents', code, params],
		queryFn: () => {
			if (!code) return undefined;
			return apis.getTrendingContents(code, params);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code,
	});
};

export const useGetTrackingCreatorContents = (code?: string, influencerCode?: string) => {
	return useQuery({
		queryKey: ['tracking-creator-contents', code, influencerCode],
		queryFn: () => {
			if (!code || !influencerCode) return undefined;
			return apis.getTrackingCreatorContents(code, influencerCode);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code && !!influencerCode,
	});
};

export const useGetInfluencers = (code?: string) => {
	return useQuery({
		queryKey: ['tracking-influencers', code],
		queryFn: () => {
			if (!code) return undefined;
			return apis.getInfluencers(code);
		},
		retry: 0,
		staleTime: 3600000,
		gcTime: Infinity,
		enabled: !!code,
	});
};

export const useContentRankingExport = () => {
	return useMutation({
		mutationFn: ({ code, params }: { code: string; params: IRankingFilterParams }) =>
			apis.getContentRankingExport(code, params),
	});
};
